import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import api from '../utils/api';

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    refresh:  <><path d="M1 4s0-3 3-3c3 0 5 2 5 2"/><path d="M1 4v4h4"/><path d="M15 12s0 3-3 3c-3 0-5-2-5-2"/><path d="M15 12v-4h-4"/></>,
    activity: <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>,
    eye:      <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
    clock:    <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
    map:      <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    globe:    <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    phone:    <><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></>,
    shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    file:     <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
    chevron:  <><polyline points="9 18 15 12 9 6"/></>,
    search:   <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    user:     <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
    list:     <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
};

function getReadingQuality(timeSpent, progress) {
  if (!timeSpent || timeSpent < 30) return { label: 'Non lu',   color: '#ff4444' };
  if (timeSpent < 60)               return { label: 'Regardé',  color: '#ff9500' };
  if (progress < 30)                return { label: 'Partiel',  color: '#ff9500' };
  if (progress < 70)                return { label: 'En cours', color: '#4db8ff' };
  return                                   { label: 'Lu',       color: '#00ff7f' };
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  if (seconds < 60) return seconds + 's';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m + 'min ' + (s > 0 ? s + 's' : '');
}

export default function LivePage() {
  const [sessions, setSessions] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, eventsRes] = await Promise.all([
        api.get('/track/active-sessions'),
        api.get('/track/events?limit=50&type=open'),
      ]);
      setSessions(sessionsRes.data);
      setRecentEvents(eventsRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const filteredEvents = recentEvents.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.member_name||'').toLowerCase().includes(s) ||
           (e.member_email||'').toLowerCase().includes(s) ||
           (e.browser||'').toLowerCase().includes(s) ||
           (e.ip||'').includes(s);
  });

  // Afficher 5 premiers, ou tous si showAll
  const visibleEvents = showAll ? filteredEvents : filteredEvents.slice(0, 5);
  const hiddenCount = filteredEvents.length - 5;

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h1 style={{ fontSize: 28, fontWeight: 900, color: '#ffffff', letterSpacing: -1 }}>
            Session <span style={{ color: '#d4f535' }}>Live</span>
          </h1>
          <button onClick={fetchData} style={{ width: 38, height: 38, borderRadius: 10, border: '1.5px solid #333', background: '#111', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="refresh" size={16} color="#d4f535" />
          </button>
        </div>
        <p style={{ color: '#555', fontSize: 12 }}>Espace de contrôle documentaire</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingTop: 14, borderTop: '1px solid #1a1a1a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d4f535', boxShadow: '0 0 8px #d4f53599' }} />
            <span style={{ fontSize: 11, color: '#888', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
              Instantané · {format(lastUpdate, 'dd MMM. yyyy à HH:mm:ss').toUpperCase()}
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#555', fontWeight: 600 }}>
            {sessions.length === 0 ? 'AUCUN SUIVI EN DIRECT' : sessions.length + ' EN DIRECT'}
          </span>
        </div>
      </div>

      {/* Bloc Activité + stats */}
      <div style={{ background: '#111', borderRadius: 14, border: '1px solid #1e1e1e', padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#1a1a00', border: '1px solid #d4f53533', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="activity" size={16} color="#d4f535" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#ffffff' }}>Activité des lecteurs</div>
            <div style={{ fontSize: 11, color: '#555' }}>Sessions et accès · {format(new Date(), 'dd MMMM yyyy')}</div>
          </div>
        </div>
        <div style={{ height: '1px', background: '#1a1a1a', marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Lecteurs actifs</span>
              <Icon name="user" size={16} color="#555" />
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: sessions.length > 0 ? '#d4f535' : '#ffffff', lineHeight: 1 }}>{sessions.length}</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>Dans l'instantané</div>
          </div>
          <div style={{ background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 12, padding: '14px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Accès récents</span>
              <Icon name="activity" size={16} color="#d4f535" />
            </div>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#d4f535', lineHeight: 1 }}>{recentEvents.length}</div>
            <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>5 affichés par défaut</div>
          </div>
        </div>
      </div>

      {/* Lectures en cours */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: '#ffffff' }}>Lectures en cours</h2>
          <span style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1 }}>{sessions.length.toString().padStart(2,'0')} SESSION</span>
        </div>
        {sessions.length === 0 ? (
          <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="eye" size={17} color="#444" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff' }}>Aucune lecture en cours</div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>Aucun lecteur actif dans votre capture.</div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {sessions.map((s, i) => {
              const progress = s.progress || 0;
              const timeSpent = s.timeSpent || 0;
              const q = getReadingQuality(timeSpent, progress);
              return (
                <div key={i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: '4px solid #d4f535', borderRadius: 14, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: '#1a1a00', border: '1.5px solid #d4f535', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, color: '#d4f535', fontWeight: 900 }}>
                        {s.memberName?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>{s.memberName}</div>
                        <div style={{ fontSize: 11, color: '#666' }}>{s.memberEmail}</div>
                      </div>
                    </div>
                    <div style={{ background: '#00ff7f22', color: '#00ff7f', border: '1px solid #00ff7f66', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00ff7f' }} /> Actif
                    </div>
                  </div>
                  {s.documentName && (
                    <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#d4f535', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="file" size={11} color="#d4f535" /> {s.documentName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#ffffff' }}>Page {s.currentPage || '?'} / {s.totalPages || '?'}</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: '#d4f535' }}>{progress}%</span>
                      </div>
                      <div style={{ background: '#1a1a1a', borderRadius: 999, height: 7, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 999, background: '#d4f535', width: progress + '%', transition: 'width 0.5s ease', boxShadow: '0 0 8px #d4f53588' }} />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                      <Icon name="clock" size={14} color="#00ff7f" />
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#00ff7f', marginTop: 4 }}>{formatDuration(timeSpent)}</div>
                      <div style={{ fontSize: 10, color: '#555' }}>Durée</div>
                    </div>
                    <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                      <Icon name="map" size={14} color="#4db8ff" />
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#4db8ff', marginTop: 4 }}>{s.city || s.country || 'FR'}</div>
                      <div style={{ fontSize: 10, color: '#555' }}>Lieu</div>
                    </div>
                    <div style={{ background: '#0d0d0d', borderRadius: 8, padding: '8px', textAlign: 'center' }}>
                      <Icon name="globe" size={14} color="#ff9500" />
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#ff9500', marginTop: 4 }}>{s.browser || 'Web'}</div>
                      <div style={{ fontSize: 10, color: '#555' }}>Navigateur</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ background: q.color + '22', color: q.color, border: '1px solid ' + q.color + '66', borderRadius: 999, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                      {q.label}
                    </div>
                    <div style={{ fontSize: 11, color: '#555' }}>{format(new Date(s.lastActivity), 'HH:mm:ss')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ height: '1px', background: '#1a1a1a', marginBottom: 16 }} />

      {/* Accès récents — 5 premiers + bouton Voir tous */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ fontSize: 14, fontWeight: 800, color: '#ffffff' }}>Accès récents</h2>
          <span style={{ fontSize: 10, color: '#555', fontWeight: 700, letterSpacing: 1 }}>
            {showAll ? filteredEvents.length : Math.min(5, filteredEvents.length)} / {filteredEvents.length} AFFICHÉS
          </span>
        </div>

        {/* Barre de recherche */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }}>
            <Icon name="search" size={14} color="#444" />
          </div>
          <input type="text" placeholder="Membre, IP, appareil..."
            value={search} onChange={e => { setSearch(e.target.value); setShowAll(false); }}
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px 10px 36px', borderRadius: 10, border: '1px solid #222', fontSize: 13, outline: 'none', background: '#111', color: '#ffffff' }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleEvents.map((e, i) => (
            <div key={i} style={{ background: '#111', border: '1px solid #1e1e1e', borderLeft: '4px solid #d4f535', borderRadius: 12, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: '#1a1a1a', border: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#d4f535', flexShrink: 0 }}>
                  {(e.member_name||'?').charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: '#ffffff' }}>{e.member_name || '—'}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>{format(new Date(e.created_at), 'dd/MM · HH:mm:ss')}</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 8 }}>Contact masqué</div>
                  <div style={{ height: '1px', background: '#1a1a1a', marginBottom: 8 }} />
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    {e.country && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#888' }}>
                        <Icon name="globe" size={12} color="#555" />
                        <span>{e.country === 'FR' ? '🇫🇷' : '🌍'} {e.country}{e.city ? ' · ' + e.city : ''}</span>
                      </div>
                    )}
                    {e.browser && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#888' }}>
                        <Icon name="globe" size={12} color="#555" />
                        <span>{e.browser}</span>
                      </div>
                    )}
                    {e.os && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#888' }}>
                        <Icon name="phone" size={12} color="#555" />
                        <span>{e.os}</span>
                      </div>
                    )}
                    {e.ip && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#4db8ff', fontFamily: 'monospace' }}>
                        <Icon name="globe" size={12} color="#4db8ff" />
                        <span>{e.ip}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bouton unique "Voir tous les détails" en bas */}
        {!showAll && hiddenCount > 0 && (
          <button onClick={() => setShowAll(true)}
            style={{ marginTop: 14, width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #d4f53566', background: '#1a1a00', color: '#d4f535', fontSize: 13, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icon name="list" size={15} color="#d4f535" />
            Voir tous les détails ({hiddenCount} de plus)
          </button>
        )}
        {showAll && (
          <button onClick={() => setShowAll(false)}
            style={{ marginTop: 14, width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #333', background: '#111', color: '#666', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Icon name="chevron" size={15} color="#666" />
            Réduire
          </button>
        )}

        {/* Note bas de page */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Icon name="shield" size={15} color="#444" />
          <div style={{ fontSize: 11, color: '#444', lineHeight: 1.6 }}>
            Contacts masqués. Les adresses IP sont visibles pour le suivi des accès. Actualisation automatique toutes les 5 secondes.
          </div>
        </div>
      </div>
    </div>
  );
}
